<template>
  <div>
    <div v-if="layout=='grid'">
      <v-container fluid class="ma-4 ml-6">
        <v-row>
          <v-card v-for="(item, index) in table.items" :key="index" class="ma-2">
            <v-card-title class="subtitle-1 pa-2 blue-grey white--text">Token</v-card-title>
            <v-card-text class="pa-4">
              <table class="token-vcard">
                <tr>
                  <td>
                    <h4>Token name:</h4>
                  </td>
                  <td>{{item.tokenName}}</td>
                </tr>
              </table>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    color="blue"
                    class="ma-auto"
                    @click="editorOpen(item.tokenName, 'summary')"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                </template>
                <span>Edit token information</span>
              </v-tooltip>
            </v-card-actions>
          </v-card>
        </v-row>
      </v-container>
    </div>
    <div v-if="layout=='list'" class="full-height">
      <v-card class="ma-2">
        <v-card-text>
          <v-container fluid class="pa-0 ma-0">
            <v-row class="pa-0 mx-2 my-0">
              <v-col class="pa-0">
                <v-text-field
                  v-model="table.search"
                  append-icon="mdi-filter"
                  label="Filter table entries"
                  single-line
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col class="pa-0 px-2">
                <v-text-field
                  label="Token name"
                  ref="newTokenName"
                  v-model="newTokenName"
                  @keypress.enter="tokenAdd"
                >
                  <template v-slot:append>
                    <v-btn
                      text
                      depressed
                      tile
                      class="ma-0"
                      @click="tokenAdd"
                      :disabled="!newTokenName"
                      outlined
                      small
                    >
                      <v-icon left>mdi-key-plus</v-icon>Add
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="auto" class="pa-0 ma-0">
                <v-btn icon @click="load()" large class="my-2 mx-0">
                  <v-icon class="ma-0">mdi-reload</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="auto" class="pa-0 ma-0">
                <v-menu v-model="settingsMenu" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" large class="my-2">
                      <v-icon>mdi-table-settings</v-icon>
                    </v-btn>
                  </template>
                  <v-card min-width="320px">
                    <v-list>
                      <v-list-item>
                        <v-list-item-title>Editor dialog box:</v-list-item-title>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-action>
                          <v-switch v-model="editor.persistent"></v-switch>
                        </v-list-item-action>
                        <v-list-item-title>Persistent mode</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-container fluid class>
          <v-data-table
            :headers="table.headers"
            :items="table.items"
            class="elevation-1"
            multi-sort
            filterable
            :options.sync="table.options"
            :search="table.search"
            :loading="table.loading"
          >
            <template v-slot:item.action="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="ma-auto"
                    @click="editorOpen(item.tokenName)"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                </template>
                <span>Edit token information</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </div>
    <v-dialog v-model="editor.active" width="unset" :persistent="editor.persistent">
      <Token
        :id="editor.id"
        @close="editorClose"
        @error="processErrorResponse"
        @success="displaySuccess"
      ></Token>
    </v-dialog>
  </div>
</template>

<style scoped>
.token-vcard > tr > td:first-child > h4 {
  white-space: nowrap;
  padding-right: 5px;
  text-align: right;
}
</style>

<script>
import Token from "../components/Token.vue";

import { getLocalParam, setLocalParam, makeMovableDialogs } from "./../lib";

makeMovableDialogs();

export default {
  name: "TokenList",
  components: { Token },
  props: {
    layout: {
      type: String,
      default: "list",
      validator: value => ["list", "grid"].indexOf(value) !== -1
    },
    id: {
      default: "",
      type: String
    }
  },
  data: () => ({
    editor: {
      id: null,
      active: false,
      persistent: getLocalParam("settings.tokenList.editor.persistent", true)
    },
    settingsMenu: false,
    newTokenName: null,
    table: {
      items: [],
      options: null,
      loading: false,
      search: "",
      index: -1,
      headers: [
        { text: "Actions", value: "action", align: "left", sortable: false },
        {
          text: "Token name",
          align: "left",
          sortable: true,
          value: "tokenName"
        }
      ]
    }
  }),
  watch: {
    id(value) {
      if (value) this.editorOpen(value);
    },
    layout(value) {
      setLocalParam("settings.tokenList.layout", value);
    },
    "editor.persistent": function(value) {
      setLocalParam("settings.tokenList.editor.persistent", value);
    },
    "table.options": {
      deep: true,
      handler(options) {
        setLocalParam("settings.tokenList.table", options);
      }
    }
  },
  created() {
    this.table.options = getLocalParam("settings.tokenList.table", {
      multiSort: true,
      mustSort: false
    });
    this.load();
    if (this.id) {
      this.editorOpen(this.id);
    }
  },
  methods: {
    tokenAdd: function() {
      this.editorOpen(this.newTokenName);
    },
    processErrorResponse(result) {
      this.$emit("error", result);
    },
    displaySuccess(result) {
      this.$emit("success", result);
    },
    load() {
      this.table.loading = true;
      this.$axios
        .get("/users/self/tokens?limit=65536")
        .then(result => {
          this.table.items = result.data.map(elt => ({
            tokenName: elt
          }));
          this.table.loading = false;
        })
        .catch(result => {
          this.processErrorResponse(result);
          this.table.loading = false;
        });
    },
    editorOpen(id) {
      if (this.editor.active) return;
      this.editor.active = true;
      this.editor.id = id;
      this.currentItemIndex = this.table.items.findIndex(
        e => e.tokenName === this.editor.id
      );
      if (this.currentItemIndex === -1) {
        this.currentItemIndex = this.table.items.length;
        this.table.items.push({
          tokenName: this.editor.id
        });
      }
    },
    editorClose(result) {
      this.editor.active = false;
      if (!result || this.currentItemIndex < 0) return;
      switch (result.type) {
        case "update":
          this.table.items.splice(this.currentItemIndex, 1);
          this.table.items.splice(this.currentItemIndex, 0, result.data);
          break;
        case "delete":
          this.table.items.splice(this.currentItemIndex, 1);
          break;
      }
      this.currentItemIndex = -1;
      if (this.id) {
        this.$router.push(`/app/tokens/${this.layout}`);
      }
    }
  }
};
</script>
